import * as constants from "@client/constants";
import * as dates from "@client/dates";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";
import * as hardware from "@client/hardware";
import type {Action, AppDispatch, GetState, RootState} from "@client/store";
import * as utils from "@client/utils";
import {Schema, server} from "@reactivated";

export type Actions =
    | {
          type: "TOGGLE_MENU";
      }
    | {
          type: "SET_HELP";
          help: string | null;
          isHelpOpen: boolean;
      }
    | {
          type: "SHOW_HELP";
      }
    | {
          type: "HIDE_HELP";
      }
    | {
          type: "START_RESOLVING";
      }
    | {
          type: "SET_RESOLVED";
          pathname: string;
          resolved: Record<string, unknown>;
      }
    | {
          type: "UPDATE_RESOLVED";
          pathname: string;
          resolved: Record<string, unknown>;
      }
    | {
          type: "ERROR_RESOLVING";
      }
    | {
          type: "START_WAITING";
      }
    | {
          type: "ERROR_WAITING";
      }
    | {
          type: "DISCONNECTED";
      }
    | {
          type: "CONNECTED";
      }
    | {
          type: "DONE_WAITING";
      }
    | {
          type: "INCREMENT_REQUESTS";
      }
    | {
          type: "DECREMENT_REQUESTS";
      }
    | {
          type: "ACTION_OUTSTANDING";
      }
    | {
          type: "ACTION_COMPLETED";
      }
    | {
          type: "START_ANIMATING";
      }
    | {
          type: "SET_NEXT_ANIMATION";
          animation: "none" | "fade" | "slide";
      }
    | {
          type: "SHOW_FEELER";
      }
    | {
          type: "ACKNOWLEDGE_FEELER";
      }
    | {
          type: "ANSWER_FEELER_POSITIVELY";
      }
    | {
          type: "ANSWER_FEELER_NEGATIVELY";
      }
    | {
          type: "AGREE_TO_RATE";
      }
    | {
          type: "DECLINE_TO_RATE";
      }
    | {
          type: "AGREE_TO_PROVIDE_FEEDBACK";
      }
    | {
          type: "HIDE_SETUP";
      }
    | {
          type: "DECLINE_TO_PROVIDE_FEEDBACK";
      };

const initialState = {
    isAnimating: false,
    hideSetup: false,
    isMenuOpen: false,
    resolved: {} as Record<string, Record<string, unknown> | null>,
    isResolving: false,
    isOnline: true,
    isAppScreens: false,
    hasOutstandingAction: false,
    outstandingRequests: 0,
    nextAnimation: "none" as "none" | "fade" | "slide" | "disabled",
    isAskingForRating: false as false | "feeler" | "rating" | "feedback",
    share: null as {
        type: Schema["rpc_share_detail_output"]["type"];
        share_uuid: string;
    } | null,
};

export const reducer = (state = initialState, action: Action): typeof initialState => {
    switch (action.type) {
        case "HIDE_SETUP":
            return {...state, hideSetup: true};
        case "START_ANIMATING":
            return {...state, isAnimating: true};
        case "SET_NEXT_ANIMATION":
            return {...state, nextAnimation: action.animation};
        case "INCREMENT_REQUESTS":
            return {...state, outstandingRequests: state.outstandingRequests + 1};
        case "DECREMENT_REQUESTS":
            return {...state, outstandingRequests: state.outstandingRequests - 1};
        case "ACTION_OUTSTANDING":
            return {...state, hasOutstandingAction: true};
        case "ACTION_COMPLETED":
            return {...state, hasOutstandingAction: false};
        case "START_WAITING":
        case "START_RESOLVING": {
            return {
                ...state,
                isResolving: true,
            };
        }
        case "UPDATE_RESOLVED": {
            const currentlyResolved = state.resolved[action.pathname];

            return {
                ...state,
                resolved: {
                    ...state.resolved,
                    [action.pathname]: {...currentlyResolved, ...action.resolved},
                },
            };
        }
        case "SET_RESOLVED": {
            return {
                ...state,
                resolved: {
                    ...state.resolved,
                    [action.pathname]: action.resolved,
                },
            };
        }
        case "DONE_WAITING":
        case "ERROR_WAITING":
        case "ERROR_RESOLVING": {
            return {
                ...state,
                isResolving: false,
            };
        }
        case "CLEAR_SHARE":
            return {...state, share: null};
        case "SHARE_SUCCESS":
            return {...state, share: action.share};
        case "TOGGLE_MENU": {
            return {...state, isMenuOpen: !state.isMenuOpen};
        }
        case "CONNECTED": {
            return {...state, isOnline: true};
        }
        case "DISCONNECTED": {
            return {...state, isOnline: false};
        }
        case "SHOW_FEELER":
            return {...state, isAskingForRating: "feeler"};
        case "ANSWER_FEELER_POSITIVELY":
            return {...state, isAskingForRating: "rating"};
        case "ANSWER_FEELER_NEGATIVELY":
            return {...state, isAskingForRating: "feedback"};
        case "AGREE_TO_RATE":
        case "DECLINE_TO_RATE":
        case "AGREE_TO_PROVIDE_FEEDBACK":
        case "DECLINE_TO_PROVIDE_FEEDBACK":
            return {...state, isAskingForRating: false};
        default: {
            return state;
        }
    }
};

const toggleMenu = (): Action => ({
    type: "TOGGLE_MENU",
});

export const hideActiveHelp = () => (dispatch: AppDispatch) => {
    dispatch({
        type: "HIDE_HELP",
    });
};

function shouldShowRating(profile: server.core.rpc.Profile.output): boolean {
    const rs = profile.settings.rating_request;

    if (rs == null) return true;
    if (rs.status === "ENABLED") return false;

    const dateOk =
        dates.differenceInDays(new Date(), dates.parseISO(rs.postponed_until)) >= 0;

    const entriesDelta = profile.entries_created - (rs.entries_created ?? 0);
    const workoutsDelta = profile.workouts_completed - (rs.workouts_completed ?? 0);
    const progressOk = entriesDelta >= 3 || workoutsDelta >= 3;

    return dateOk && progressOk;
}

export const showFeelerIfFirstTime =
    () => (dispatch: AppDispatch, getState: GetState) => {
        if (
            typeof localStorage !== "undefined" &&
            localStorage.getItem("FORCE_RATINGS_REQUEST") != null
        ) {
            localStorage.removeItem("FORCE_RATINGS_REQUEST");
            dispatch({
                type: "SHOW_FEELER",
            });
            return;
        }

        const state = getState();
        const profile = orm.getProfile(state)!;
        const now = new Date();
        const dateJoined = dates.parseISO(profile.user.date_joined);
        const accountAge = dates.differenceInDays(now, dateJoined);

        if (
            window.cordova != null &&
            (profile.entries_created >
                constants.NUMBER_OF_ENTRIES_BEFORE_ASKING_FOR_RATING ||
                profile.workouts_completed >
                    constants.NUMBER_OF_WORKOUTS_BEFORE_ASKING_FOR_RATING) &&
            accountAge > constants.ACCOUNT_AGE_IN_DAYS_BEFORE_ASKING_FOR_RATING &&
            shouldShowRating(profile)
        ) {
            dispatch({
                type: "SHOW_FEELER",
            });
        }
    };

const showHelp = (): Action => ({
    type: "SHOW_HELP",
});

export const getIsLoading = (state: RootState) => {
    return (
        state.scenes.isResolving ||
        state.search.isSearching ||
        state.search.isScanning ||
        (state.scenes.hasOutstandingAction && state.scenes.outstandingRequests > 0)
    );
};

const saveRatingRequest =
    (obj: server.core.rpc.Profile.output["settings"]["rating_request"]) =>
    (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();
        const profile = orm.getProfile(state)!;
        const updatedSettings = {...profile.settings, rating_request: obj};
        void server.core.rpc.update_settings(updatedSettings);
        dispatch({
            type: "SET_PROFILE",
            profile: {...profile, settings: updatedSettings},
        });
    };

const postponeRating = () => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const profile = orm.getProfile(state)!;
    const prev = profile.settings.rating_request;
    const prevPostponements = prev && prev.status === "LATER" ? prev.postponements : 0;
    const postponements = prevPostponements + 1;

    // Linear backoff: 45 days + 14 days for each previous postponement
    const daysToPostpone = 45 + prevPostponements * 14;

    const postponed_until = dates.formatISO(dates.addDays(new Date(), daysToPostpone), {
        representation: "date",
    });

    dispatch(
        saveRatingRequest({
            status: "LATER",
            postponed_until,
            postponements,
            entries_created: profile.entries_created,
            workouts_completed: profile.workouts_completed,
        }),
    );
};

const ratingActions = {
    answerFeelerPositively: () => async (dispatch: AppDispatch) => {
        void hardware.haptic();
        dispatch({type: "ANSWER_FEELER_POSITIVELY"});
        await utils.delay(2000);
        dispatch(saveRatingRequest({status: "ENABLED"}));
        void hardware.RateApp.requestReview();
        await utils.delay(2000);
        // AGREE_TO_RATE action will set isAskingForRating to false, hiding the prompt.
        dispatch({type: "AGREE_TO_RATE"});
    },
    answerFeelerNegatively: () => (dispatch: AppDispatch) => {
        void hardware.haptic();
        dispatch({type: "ANSWER_FEELER_NEGATIVELY"});
    },
    // No longer used, we show the prompt when they rate us positively.
    // agreeToRate: () => (dispatch: AppDispatch) => {
    //     void hardware.haptic();
    //     dispatch(saveRatingRequest({status: "ENABLED"}));
    //     dispatch({type: "AGREE_TO_RATE"});
    //     void hardware.RateApp.requestReview();
    // },
    declineToRate: () => (dispatch: AppDispatch) => {
        void hardware.haptic();
        dispatch(postponeRating());
        dispatch({type: "DECLINE_TO_RATE"});
    },
    agreeToProvideFeedBack: () => async (dispatch: AppDispatch) => {
        void hardware.haptic();
        dispatch({
            type: "AGREE_TO_PROVIDE_FEEDBACK",
        });
        const router = await import("@client/router");
        void router.push("contact.feedback", {});
        dispatch(postponeRating());
        dispatch(
            notify("Anything goes. We take all feedback seriously.", {type: "info"}),
        );
    },
    declineToProvideFeedBack: () => (dispatch: AppDispatch) => {
        void hardware.haptic();
        dispatch(postponeRating());
        dispatch({type: "DECLINE_TO_PROVIDE_FEEDBACK"});
    },
};

export type RATING_ACTIONS = typeof ratingActions;

export const actions = {
    showHelp,
    toggleMenu,
    getIsLoading,
    ...ratingActions,
};
